/**
 * Token utility functions for handling JWT decoding and token payload extraction
 * Used by the router for authentication and authorization
 */

/**
 * Decode base64url string to UTF-8 text (handles special characters correctly)
 * @param base64url - Base64url encoded string
 * @returns Decoded UTF-8 string
 */
export const base64urlDecode = (base64url: string): string => {
  // Convert base64url to base64
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  // Add padding if necessary
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const paddedBase64 = base64 + padding

  // Check if we're in a Node.js environment (e.g., Jest tests)
  if (typeof Buffer !== 'undefined' && typeof process !== 'undefined' && process.versions?.node) {
    // Use Node.js Buffer for proper UTF-8 decoding
    return Buffer.from(paddedBase64, 'base64').toString('utf-8')
  }

  // Browser environment: Decode using atob but handle UTF-8 properly
  const binaryString = atob(paddedBase64)

  // Convert binary string to UTF-8 using Uint8Array
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  // Use TextDecoder to properly decode UTF-8
  return new TextDecoder('utf-8').decode(bytes)
}

/**
 * Helper to decode JWT token (handles both OIDC raw strings and Amplify v6 token objects)
 *
 * @param token - Either a JWT string or an Amplify v6 token object
 * @returns Decoded token payload or empty object if decoding fails
 */
export const decodeJWT = (token: any): any => {
  if (!token) return {}
  if (typeof token === 'string') {
    try {
      const payload = token.split('.')[1]
      const decoded = base64urlDecode(payload)
      return JSON.parse(decoded)
    } catch {
      return {}
    }
  }
  // Already decoded (Amplify v6 token object)
  return token.payload || {}
}

/**
 * Check if a JWT token is expired
 * @param token - JWT token to check (either string or decoded object)
 * @returns true if token is valid (not expired), false otherwise
 */
export const isTokenValid = (token: any): boolean => {
  if (!token) return false

  try {
    const payload = decodeJWT(token)

    if (payload.exp) {
      const expirationTime = payload.exp * 1000 // Convert to milliseconds
      return Date.now() < expirationTime
    }
  } catch (error) {
    console.warn('Failed to check token expiry:', error)
    return false
  }

  // If no expiration info found, consider it invalid to be safe
  return false
}

/**
 * Extract token payload based on authentication method
 *
 * For regular Cognito User Pool users:
 * - Groups are stored in the access token
 * - Use existing behavior (access token only)
 *
 * For OIDC/Panex users:
 * - Groups are stored in the ID token (not access token)
 * - Merge both token groups and remove duplicates
 *
 * @param session - The current authentication session
 * @returns Decoded token payload containing user claims including cognito:groups
 */
export const extractTokenPayload = (session: any): any => {
  if (!session) return {}

  if (session.authMethod === 'OIDC') {
    // OIDC/Panex users: merge groups from both ID token and access token
    const accessTokenPayload = decodeJWT(session.tokens?.accessToken)
    const idTokenPayload = decodeJWT(session.tokens?.idToken)

    // Merge groups from both tokens and remove duplicates
    const idTokenGroups = Array.isArray(idTokenPayload['cognito:groups']) ? idTokenPayload['cognito:groups'] : []
    const accessTokenGroups = Array.isArray(accessTokenPayload['cognito:groups'])
      ? accessTokenPayload['cognito:groups']
      : []
    const mergedGroups = Array.from(new Set([...idTokenGroups, ...accessTokenGroups]))

    return {
      ...accessTokenPayload,
      'cognito:groups': mergedGroups,
    }
  }

  // Regular Cognito User Pool: groups are in access token (existing behavior)
  return decodeJWT(session.tokens?.accessToken)
}
